import React, { useEffect, useState } from 'react';
import { Alert, Box, Chip, CircularProgress, Collapse, IconButton, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RoundedPaper from 'shared/components/RoundedPaper';
import AgentHubService from 'services/AgentHubService';
import { fmtTime, StatusChip } from './parts';
import { ApprovalRow, PipelinePrRow, RunRow, parseVariant } from './types';

const service = new AgentHubService();
// Rows per page, so the tab stays a screen or two long however many PRs and approvals there are.
const PR_PAGE = 20;
const APPROVAL_PAGE = 10;
const KIND_LABEL: Record<ApprovalRow['kind'], string> = { live_action: 'Live action', merge: 'Merge', revert: 'Revert', proposal: 'Proposal' };

const STAGE_LOOK: Record<string, { label: string; fg: string; bg: string }> = {
  prep: { label: 'Prep', fg: '#1E40AF', bg: '#DBEAFE' },
  review: { label: 'Review', fg: '#1E40AF', bg: '#DBEAFE' },
  awaiting_ci: { label: 'Waiting for CI', fg: '#475569', bg: '#F1F5F9' },
  awaiting_merge: { label: 'Waiting for your OK', fg: '#92400E', bg: '#FEF3C7' },
  needs_human: { label: 'Needs a person', fg: '#991B1B', bg: '#FEE2E2' },
  merged: { label: 'Merged', fg: '#065F46', bg: '#D1FAE5' },
  deploying: { label: 'Deploying', fg: '#1E40AF', bg: '#DBEAFE' },
  watching: { label: 'Watching deploy', fg: '#1E40AF', bg: '#DBEAFE' },
  watch_24h: { label: '24h check pending', fg: '#475569', bg: '#F1F5F9' },
  done: { label: 'Done', fg: '#065F46', bg: '#D1FAE5' },
  reverted: { label: 'Reverted', fg: '#9A3412', bg: '#FFEDD5' },
  closed: { label: 'Closed', fg: '#475569', bg: '#F1F5F9' },
};
const chip = (stage: string) => { const s = STAGE_LOOK[stage] ?? { label: stage, fg: '#475569', bg: '#F1F5F9' }; return <Chip size="small" label={s.label} sx={{ height: 20, fontSize: 11, fontWeight: 600, color: s.fg, bgcolor: s.bg }} />; };

function PrRow({ p }: { p: PipelinePrRow }) {
  const [open, setOpen] = useState(false);
  const [runs, setRuns] = useState<RunRow[] | null>(null);
  const stages = parseVariant<Array<{ stage: string; at: string; note: string }>>(p.stages, []);
  const gate = parseVariant<{ pass: boolean; mode: string; failing: string[]; rules: Array<{ rule: string; ok: boolean; detail: string }> } | null>(p.gate, null);
  const [runsError, setRunsError] = useState<string | null>(null);
  useEffect(() => { if (open && !runs && !runsError) service.prRuns(p.subject).then(setRuns).catch(e => setRunsError(e.message)); }, [open, runs, runsError, p.subject]);
  return (
    <>
      <TableRow hover onClick={() => setOpen(o => !o)} sx={{ cursor: 'pointer', verticalAlign: 'top' }}>
        <TableCell sx={{ maxWidth: 420 }}>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
            <IconButton size="small" aria-label={open ? `Hide ${p.subject} history` : `Show ${p.subject} history`} aria-expanded={open} sx={{ mt: -0.5 }}>{open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}</IconButton>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.url ? <a href={p.url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>{p.subject}</a> : p.subject} <Box component="span" sx={{ fontWeight: 400 }}>{p.title}</Box></Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>by {p.author} · {stages.at(-1)?.note ?? ''}</Typography>
            </Box>
          </Box>
        </TableCell>
        <TableCell>{chip(p.stage)}</TableCell>
        <TableCell>{p.verdict ?? '—'}</TableCell>
        <TableCell sx={{ maxWidth: 280 }}><Typography variant="caption">{gate ? (gate.pass ? `all rules met${gate.mode === 'shadow' ? ' (shadow)' : ''}` : `not met: ${gate.failing.join(', ')}`) : '—'}</Typography></TableCell>
        <TableCell><Typography variant="caption" sx={{ color: 'text.secondary' }}>{fmtTime(p.updated_at)}</Typography></TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={5} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open}>
            <Box sx={{ py: 1.5, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <Box>
                <Typography variant="subtitle2">History</Typography>
                {stages.map((s, i) => <Typography key={i} component="div" variant="body2" sx={{ mt: 0.25 }}><Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>{fmtTime(s.at)}</Box>{chip(s.stage)} {s.note}</Typography>)}
              </Box>
              <Box>
                <Typography variant="subtitle2">Agent runs</Typography>
                {runsError && <Typography variant="body2" color="error">{runsError}</Typography>}
                {!runs && !runsError && <CircularProgress size={16} />}
                {runs?.map(r => <Typography key={r.run_id} component="div" variant="body2" sx={{ mt: 0.25 }}><StatusChip status={r.status} /> <RouterLink to={`/agents/${r.agent}?run=${encodeURIComponent(r.run_id)}`}>{r.agent}</RouterLink> · {fmtTime(r.started_at)} · {r.summary ?? r.error ?? ''}</Typography>)}
                {gate && <><Typography variant="subtitle2" sx={{ mt: 1.5 }}>Merge gate ({gate.mode})</Typography>{gate.rules.map(r => <Typography key={r.rule} variant="caption" sx={{ display: 'block' }}>{r.ok ? '✅' : '❌'} {r.rule} — {r.detail}</Typography>)}</>}
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

/** PRs moving through prep → review → merge → deploy watch, and the approvals waiting in Slack. */
export default function PipelineTab() {
  const [openOnly, setOpenOnly] = useState(true);
  const [prs, setPrs] = useState<PipelinePrRow[] | null>(null);
  const [approvals, setApprovals] = useState<ApprovalRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [prPage, setPrPage] = useState(0);
  const [approvalPage, setApprovalPage] = useState(0);
  useEffect(() => {
    setPrs(null);
    setPrPage(0);
    Promise.all([service.prs(openOnly), service.approvals()]).then(([p, a]) => { setPrs(p); setApprovals(a); }).catch(e => setError(e.message));
  }, [openOnly]);
  const pending = (approvals ?? []).filter(a => a.status === 'pending');
  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {pending.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <b>{pending.length} waiting for approval in Slack</b> (decide there — only the approvers' clicks count):
          {pending.map(a => {
            const ref = parseVariant<{ channel: string; ts: string } | null>(a.slack, null);
            return <Box key={a.approval_id} sx={{ fontSize: 13 }}>{KIND_LABEL[a.kind]}: {a.title} · asked by {a.agent} · expires {fmtTime(a.expires_at)}{ref ? <> · <a href={`https://slack.com/app_redirect?channel=${ref.channel}`} target="_blank" rel="noreferrer">open in Slack</a></> : ''}</Box>;
          })}
        </Alert>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>Pull requests</Typography>
        <ToggleButtonGroup size="small" exclusive value={openOnly ? 'open' : 'all'} onChange={(_e, v) => v && setOpenOnly(v === 'open')}>
          <ToggleButton value="open">In progress</ToggleButton>
          <ToggleButton value="all">All</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <RoundedPaper sx={{ p: 0, overflowX: 'auto', mb: 3 }}>
        {!prs && <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} /></Box>}
        {prs?.length === 0 && <Typography variant="body2" sx={{ p: 2, color: 'text.secondary' }}>No pull requests in the pipeline.</Typography>}
        {prs && prs.length > 0 && (
          <Table size="small">
            <TableHead><TableRow><TableCell>PR</TableCell><TableCell>Stage</TableCell><TableCell>Review</TableCell><TableCell>Merge gate</TableCell><TableCell>Updated</TableCell></TableRow></TableHead>
            <TableBody>{prs.slice(prPage * PR_PAGE, (prPage + 1) * PR_PAGE).map(p => <PrRow key={p.subject} p={p} />)}</TableBody>
          </Table>
        )}
        {prs && prs.length > PR_PAGE && (
          <TablePagination component="div" count={prs.length} page={prPage} onPageChange={(_e, n) => setPrPage(n)} rowsPerPage={PR_PAGE} rowsPerPageOptions={[]} />
        )}
      </RoundedPaper>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Recent approvals</Typography>
      <RoundedPaper sx={{ p: 0, overflowX: 'auto' }}>
        <Table size="small">
          <TableHead><TableRow><TableCell>What</TableCell><TableCell>Asked by</TableCell><TableCell>Status</TableCell><TableCell>Asked</TableCell><TableCell>Decided</TableCell></TableRow></TableHead>
          <TableBody>
            {(approvals ?? []).slice(approvalPage * APPROVAL_PAGE, (approvalPage + 1) * APPROVAL_PAGE).map(a => (
              <TableRow key={a.approval_id}>
                <TableCell sx={{ maxWidth: 420 }}><Typography variant="body2">{KIND_LABEL[a.kind]}: {a.title}</Typography><Typography variant="caption" sx={{ color: 'text.secondary' }}>{a.impact}</Typography></TableCell>
                <TableCell>{a.agent}</TableCell>
                <TableCell>{a.status}</TableCell>
                <TableCell><Typography variant="caption">{fmtTime(a.requested_at)}</Typography></TableCell>
                <TableCell><Typography variant="caption">{a.decided_at ? fmtTime(a.decided_at) : '—'}</Typography></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {(approvals?.length ?? 0) > APPROVAL_PAGE && (
          <TablePagination component="div" count={approvals!.length} page={approvalPage} onPageChange={(_e, n) => setApprovalPage(n)} rowsPerPage={APPROVAL_PAGE} rowsPerPageOptions={[]} />
        )}
      </RoundedPaper>
    </Box>
  );
}
